import {FmlxToast} from 'fmlx-common-ui';
import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {hot, setConfig} from 'react-hot-loader';
import {connect, useSelector} from 'react-redux';
import {bindActionCreators} from 'redux';

import FastStatusbar from 'contents/FastStatusBar/FastStatusBar';
import {initializeHumidifierState} from 'stores/business/humidifierBusiness';
import {initializePlateRecognitionState} from 'stores/business/plateRecognitionBusiness';
import {getSoftwareVersionAndUpdateInfo} from 'api/rest/softwareUpdaterApi';
import {selectIsInstrumentOperational} from 'stores/selectors/instrumentSelectors';
import {getServiceOperationalStatus} from 'stores/business/instrumentBusiness';
import {setSoftwareReloadAttemptFromLocalStorage} from 'stores/business/appBusiness';
import FastBanner from 'components/FastBanner/FastBanner';
import PageContent from './Main/PageContent';
import FastModal from '../components/FastModal';
import DispenseReportNotification from './DispenseReport/DispenseReportNotification';
import LoadingView from '../components/LoadingView/LoadingView';
import ModalLicenseActivation from './Licensing/ModalLicenseActivation';
import DemoOnlyFloatingMessage from './Licensing/DemoOnlyFloatingMessage';
import LicenseCountdown from './Licensing/LicenseCountdown';

import './App.scss';

import {generatePallete} from '../lib/colorHelper';
import SideMenuContainer from './SideMenu/SideMenuContainer';
import {
  buildMainHubs,
  buildBackupAndRestoreHub,
  buildInstrumentHub,
  buildUpdaterHub,
} from '../stores/business/hubBuilderBusiness';
import {getConfig, getDeviceConfig} from '../stores/business/configStateBusiness';
import {updateSessionActiveStatus} from '../stores/business/sessionBusiness';

import ModalSetTrayID from '../components/ModalSetTrayID';
import ManualSetTipLocation from '../components/ManualSetTipLocation/ManualSetTipLocation';
import {setColorPalette} from '../stores/actions/colorAction';

import {AllowedPagesForBanner, Feedback, ViewMode} from '../constants/dataEnum';
import {showLoading, hideLoading, hideSnackbar} from '../stores/actions/appStateAction';
import {
  handleSoftwareUpdateSuccess,
  validateSoftwareUpdateResult,
  handleSoftwareUpdateFailed,
} from '../stores/business/softwareUpdaterBusiness';
import {startSendUILogToBackendSchedule} from '../stores/business/errorReportingBusiness';

import withIntl from '../lib/withIntl';
import {isOnSetupWizardView} from '../stores/business/setupWizardBusiness/commonSetupWizardBusiness';
import {recoverRunningProtocol} from '../stores/business/runtimeBusiness';
import {removeTemporaryState} from '../stores/actions/temporaryStateAction';
import {getLicenseData} from '../stores/business/licenseBusiness';
import TypedModalContainer from '../components/Modal/TypedModalContainer';

setConfig({
  showReactDomPatchNotification: false,
});

function App({
  buildMainHubs,
  buildUpdaterHub,
  buildInstrumentHub,
  getDeviceConfig,
  sessionBusinessUpdateSessionActiveStatus,
  configStateHubHost,
  logMaxStackSize,
  modalState,
  isOverrideBeforeUnload,
  snackbar,
  loading,
  appStateViewMode,
  commonSetupWizardIsOnSetupWizardView,
  colorActionSetColorPalette,
  dispenseReportNotification,
  runtimeStateBusinessRecoverRunningProtocol,
  temporaryStateActionRemoveTemporaryState,
  appStateShowLoading,
  appStateHideLoading,
  appStateHideSnackbar,
  triggerRebuildConnection,
  startSendUILogToBackendSchedule,
  protocolEditorAutosaveState,
  protocolEditorIsPendingSaveExistState,
  licenseBusinessGetLicenseData,
  intl,
  buildBackupAndRestoreHub,
  initializeHumidifierState,
  initializePlateRecognitionState,
  validateSoftwareUpdateResult,
  handleSoftwareUpdateSuccess,
  handleSoftwareUpdateFailed,
  bannerState,
  getServiceOperationalStatus,
  getConfig,
}) {
  const [hubsConnected, setHubsConnected] = useState(false);
  const isInstrumentOperational = useSelector(selectIsInstrumentOperational);

  const onUnload = useCallback(() => {
    sessionBusinessUpdateSessionActiveStatus(false);
  }, [sessionBusinessUpdateSessionActiveStatus]);

  const alertUser = (e) => {
    e.preventDefault();
    e.returnValue = '';
  };

  useEffect(() => {
    const allowedViewModeToLeave = [ViewMode.LOGIN, ViewMode.LOGIN_CONFIRMATION, ViewMode.ABOUT];

    const isPreventLeave = !allowedViewModeToLeave.includes(appStateViewMode);

    if (isPreventLeave && !isOverrideBeforeUnload) {
      window.addEventListener('beforeunload', alertUser);
    } else {
      window.removeEventListener('beforeunload', alertUser);
    }

    return () => window.removeEventListener('beforeunload', alertUser);
  }, [
    protocolEditorAutosaveState,
    protocolEditorIsPendingSaveExistState,
    loading,
    appStateViewMode,
    isOverrideBeforeUnload,
  ]);

  // #region software update
  useEffect(() => {
    const handleSWUpdateTask = async () => {
      const softwareVersionInfo = await getSoftwareVersionAndUpdateInfo();
      const {isSoftwareUpToDate, isSoftwareUpdateFailed, errorDuringValidation} =
        validateSoftwareUpdateResult(softwareVersionInfo);

      if (errorDuringValidation) return;
      if (isSoftwareUpdateFailed) {
        handleSoftwareUpdateFailed(softwareVersionInfo);
      } else {
        handleSoftwareUpdateSuccess(softwareVersionInfo, isSoftwareUpToDate);
      }
    };

    if (configStateHubHost.urlInstrumentControlLocation && isInstrumentOperational) {
      handleSWUpdateTask();
    }
  }, [
    configStateHubHost,
    validateSoftwareUpdateResult,
    handleSoftwareUpdateSuccess,
    handleSoftwareUpdateFailed,
    isInstrumentOperational,
  ]);

  useEffect(() => {
    if (!triggerRebuildConnection) {
      return;
    }

    const doBuild = () => {
      setSoftwareReloadAttemptFromLocalStorage();
      window.location.reload(true);
    };

    doBuild();
  }, [triggerRebuildConnection]);
  // #endregion software update

  // #region init main app
  useEffect(() => {
    const initMainAppConfigAndHub = async () => {
      appStateShowLoading(intl.formatMessage({id: 'hubBuilderBusiness.build.loading'}));
      try {
        colorActionSetColorPalette(generatePallete(96));
        await getDeviceConfig();
        await buildMainHubs();
        await getServiceOperationalStatus();
        await getConfig();
        setHubsConnected(true);
        appStateHideLoading();
      } catch (error) {
        console.error('Main app init failed: ', error);
      } finally {
        appStateHideLoading();
      }
    };

    initMainAppConfigAndHub();
    window.addEventListener('unload', onUnload);

    return () => {
      window.removeEventListener('unload', onUnload);
    };
  }, [
    appStateHideLoading,
    appStateShowLoading,
    colorActionSetColorPalette,
    getDeviceConfig,
    getServiceOperationalStatus,
    buildMainHubs,
    intl,
    onUnload,
    getConfig,
  ]);
  // #endregion init main app

  // #region init instrument
  useEffect(() => {
    const initInstrumentConfigAndHub = async () => {
      try {
        const [instrumentHub] = await Promise.allSettled([
          buildInstrumentHub(),
          buildUpdaterHub(),
          buildBackupAndRestoreHub(),
        ]);

        if (instrumentHub.status === 'fulfilled') {
          licenseBusinessGetLicenseData();
          initializeHumidifierState();
          await initializePlateRecognitionState();
        }
      } catch (error) {
        console.error('Instrument hubs init failed: ', error);
      } finally {
        appStateHideLoading();
      }
    };

    if (configStateHubHost.urlInstrumentControlLocation && isInstrumentOperational) {
      initInstrumentConfigAndHub();
    }
  }, [
    configStateHubHost,
    buildUpdaterHub,
    buildInstrumentHub,
    appStateHideLoading,
    licenseBusinessGetLicenseData,
    buildBackupAndRestoreHub,
    initializeHumidifierState,
    initializePlateRecognitionState,
    isInstrumentOperational,
  ]);
  // #endregion init instrument

  useEffect(() => {
    if (!logMaxStackSize) {
      return;
    }

    startSendUILogToBackendSchedule(logMaxStackSize);
  }, [logMaxStackSize, startSendUILogToBackendSchedule]);

  return (
    <>
      <LoadingView
        show={loading.show}
        text={loading.text}
        darkMode={commonSetupWizardIsOnSetupWizardView()}
        hideLogo={commonSetupWizardIsOnSetupWizardView()}
        mode={commonSetupWizardIsOnSetupWizardView() ? 'Contained' : 'FullScreen'}
        onCancel={loading.onCancel}
      />
      <div
        id="container"
        onContextMenu={(evt) => {
          evt.preventDefault();
        }}
      >
        <FmlxToast
          open={!snackbar.show && dispenseReportNotification && dispenseReportNotification?.show}
          content={
            <DispenseReportNotification
              onSeeDetails={() => {
                runtimeStateBusinessRecoverRunningProtocol();
                temporaryStateActionRemoveTemporaryState('dispenseReportNotification');
              }}
            />
          }
          variant={Feedback.SUCCESS}
          onClick={() => {
            temporaryStateActionRemoveTemporaryState('dispenseReportNotification');
          }}
        />
        <FmlxToast
          open={snackbar.show}
          content={snackbar.text}
          variant={snackbar.style}
          autoHideDuration={snackbar.timeout}
          disableAutoHide={!snackbar?.autoHide}
          setOpen={!snackbar?.autoHide && snackbar.disableBlur ? () => {} : appStateHideSnackbar}
          useActionButton={snackbar.useActionButton}
          hideAction={!snackbar.useActionButton}
          onClick={snackbar.actionButton}
          buttonLabel={snackbar.buttonLabel}
          disableBlur={snackbar.disableBlur}
          title={snackbar.title}
        />
        <FastModal
          header={modalState.header}
          content={modalState.content}
          btnAction={modalState.btnAction}
          show={modalState.show}
          isValid={modalState.isValid}
          onClose={modalState.onClose}
          className={modalState.className}
          closeDisabled={modalState.closeDisabled}
          footerContent={modalState.footerContent}
          backdrop={modalState.backdrop}
          errorCodeNumber={modalState.errorCodeNumber}
          size={modalState.modalSize}
          style={modalState.style}
          confirmationCheckbox={modalState.confirmationCheckbox}
        />
        {bannerState.show && AllowedPagesForBanner.includes(appStateViewMode) && (
          <FastBanner severity={bannerState.severity} title={bannerState.title} textAlign={bannerState.textAlign} />
        )}
        <ModalLicenseActivation />
        <DemoOnlyFloatingMessage />
        <LicenseCountdown />
        <ModalSetTrayID />
        <ManualSetTipLocation />
        <TypedModalContainer />
        {hubsConnected && <PageContent />}
        {hubsConnected && <FastStatusbar />}
        <SideMenuContainer />
      </div>
    </>
  );
}

function mapStateToProps(state) {
  return {
    loading: state.appState.loading,
    snackbar: state.appState.snackbar,
    appStateViewMode: state.appState.viewMode,
    isOverrideBeforeUnload: state.appState.isOverrideBeforeUnload,
    modalState: state.modalState,
    configStateHubHost: state.configState.hubHost,
    logMaxStackSize: state.configState.logMaxStackSize,
    dispenseReportNotification: state.temporaryState.dispenseReportNotification,
    triggerRebuildConnection: state.appState.triggerRebuildConnection,
    protocolEditorAutosaveState: state.protocolEditorState.autosave,
    protocolEditorIsPendingSaveExistState: state.protocolEditorState.isPendingSaveExist,
    bannerState: state.bannerState,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    buildMainHubs: bindActionCreators(buildMainHubs, dispatch),
    buildUpdaterHub: bindActionCreators(buildUpdaterHub, dispatch),
    buildInstrumentHub: bindActionCreators(buildInstrumentHub, dispatch),
    getDeviceConfig: bindActionCreators(getDeviceConfig, dispatch),
    sessionBusinessUpdateSessionActiveStatus: bindActionCreators(updateSessionActiveStatus, dispatch),
    colorActionSetColorPalette: bindActionCreators(setColorPalette, dispatch),
    runtimeStateBusinessRecoverRunningProtocol: bindActionCreators(recoverRunningProtocol, dispatch),
    temporaryStateActionRemoveTemporaryState: bindActionCreators(removeTemporaryState, dispatch),
    appStateShowLoading: bindActionCreators(showLoading, dispatch),
    appStateHideLoading: bindActionCreators(hideLoading, dispatch),
    appStateHideSnackbar: bindActionCreators(hideSnackbar, dispatch),
    startSendUILogToBackendSchedule: bindActionCreators(startSendUILogToBackendSchedule, dispatch),
    commonSetupWizardIsOnSetupWizardView: bindActionCreators(isOnSetupWizardView, dispatch),
    licenseBusinessGetLicenseData: bindActionCreators(getLicenseData, dispatch),
    buildBackupAndRestoreHub: bindActionCreators(buildBackupAndRestoreHub, dispatch),
    initializeHumidifierState: bindActionCreators(initializeHumidifierState, dispatch),
    initializePlateRecognitionState: bindActionCreators(initializePlateRecognitionState, dispatch),
    validateSoftwareUpdateResult: bindActionCreators(validateSoftwareUpdateResult, dispatch),
    handleSoftwareUpdateSuccess: bindActionCreators(handleSoftwareUpdateSuccess, dispatch),
    handleSoftwareUpdateFailed: bindActionCreators(handleSoftwareUpdateFailed, dispatch),
    getServiceOperationalStatus: bindActionCreators(getServiceOperationalStatus, dispatch),
    getConfig: bindActionCreators(getConfig, dispatch),
  };
}

export default process.env.NODE_ENV === 'development'
  ? hot(module)(connect(mapStateToProps, mapDispatchToProps)(withIntl(App)))
  : connect(mapStateToProps, mapDispatchToProps)(withIntl(App));

App.propTypes = {
  loading: PropTypes.object.isRequired,
  snackbar: PropTypes.object.isRequired,
  modalState: PropTypes.object.isRequired,
  isOverrideBeforeUnload: PropTypes.bool.isRequired,
  appStateViewMode: PropTypes.string.isRequired,
  buildMainHubs: PropTypes.func.isRequired,
  buildUpdaterHub: PropTypes.func.isRequired,
  buildInstrumentHub: PropTypes.func.isRequired,
  getDeviceConfig: PropTypes.func.isRequired,
  sessionBusinessUpdateSessionActiveStatus: PropTypes.func.isRequired,
  configStateHubHost: PropTypes.object.isRequired,
  logMaxStackSize: PropTypes.any,
  colorActionSetColorPalette: PropTypes.func.isRequired,
  dispenseReportNotification: PropTypes.object,
  runtimeStateBusinessRecoverRunningProtocol: PropTypes.func.isRequired,
  temporaryStateActionRemoveTemporaryState: PropTypes.func.isRequired,
  appStateShowLoading: PropTypes.func.isRequired,
  appStateHideLoading: PropTypes.func.isRequired,
  appStateHideSnackbar: PropTypes.func.isRequired,
  startSendUILogToBackendSchedule: PropTypes.func.isRequired,
  triggerRebuildConnection: PropTypes.string,
  commonSetupWizardIsOnSetupWizardView: PropTypes.func.isRequired,
  protocolEditorAutosaveState: PropTypes.bool.isRequired,
  protocolEditorIsPendingSaveExistState: PropTypes.bool.isRequired,
  licenseBusinessGetLicenseData: PropTypes.func.isRequired,
  intl: PropTypes.any.isRequired,
  buildBackupAndRestoreHub: PropTypes.func.isRequired,
  initializeHumidifierState: PropTypes.func.isRequired,
  initializePlateRecognitionState: PropTypes.func.isRequired,
  validateSoftwareUpdateResult: PropTypes.func.isRequired,
  handleSoftwareUpdateSuccess: PropTypes.func.isRequired,
  handleSoftwareUpdateFailed: PropTypes.func.isRequired,
  bannerState: PropTypes.object.isRequired,
  getServiceOperationalStatus: PropTypes.func.isRequired,
  getConfig: PropTypes.func.isRequired,
};

App.defaultProps = {
  dispenseReportNotification: {},
  triggerRebuildConnection: null,
  logMaxStackSize: null,
};
